import React, { memo, useContext, useMemo, useCallback, Fragment } from 'react';
import { Button } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  CallRounded as CallIcon,
  MessageRounded as MessageIcon,
  EmailRounded as EmailIcon,
} from '@material-ui/icons';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  PATIENT, PRODUCT_INFO, DIMENSION, MODAL_KEY,
  CONFIRMATION_POPUP_KEY, RESPONSE_POPUP_KEY,
} from '../../../constants/patientChart.constant';
import { POPUP_TYPE } from '../../subComponents/popup';
import Modal from '../../subComponents/commonModal/v3';
import { getDateTime } from '../../../utils/time';
import { CallPatient } from '../CallPatient/v1';
import { TextPatient } from '../TextPatient/v1';
import { EmailPatient } from '../EmailPatient/v1';

export const PatientInformation = memo(({
  orderId = '',
  patientId = '',
  doctorId = '',
  patient = PATIENT(),
  product = PRODUCT_INFO(),
  calling = {},
  messageTemplates = {
    data: []
  },
  emailTemplates = {
    data: [],
    subjects: {
      data: new Map(),
    },
  },
  emailTemplatesV2 = {
    data: [],
    subjects: {
      data: new Map(),
    },
  },
  emailReasons = {
    data: [],
  },
  callSummaryReasons = [],
  callPatientInfo = {},
  sendTextInfo = {},
  sendEmailInfo = {},
  hasActions = false,
  callPatient = async () => {},
  getNotesTabInfo = async () => {},
  saveCallSummary = async () => {},
  sendText = async () => {},
  sendEmail = async () => {},
  getEmails = async () => {},
}) => {
  const {
    ModalCollection,
    handleModalOpen,
    handleModalClose,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);
  const { formatted: { date, time } } = getDateTime({ date_UTC: patient.StartDateTime_UTC });

  const StartTime = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, (
      <span className='bold'>
        {time} - {date}
      </span>
    )]
  ]);
  const Name = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.FirstName} {patient.LastName}</span>]
  ]);
  const Storefront = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{product.StoreFrontData.WorkingName}</span>]
  ]);
  const Shipping = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.City}, {patient.State}</span>]
  ]);
  const Dob = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.DOB} (age: {patient.Age})</span>]
  ]);
  const Gender = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.Gender}</span>]
  ]);
  const Height = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.Height}"</span>]
  ]);
  const Weight = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.Weight} lb</span>]
  ]);
  const BMI = new Map([
    [true, <Skeleton variant='text' className='flex flex-fill' />],
    [false, <span className='bold'>{patient.BMI}</span>]
  ]);

  const ButtonsWithConfig = useMemo(() => [
    {
      Icon: CallIcon,
      Component: CallPatient,
      label: 'Call',
      key: MODAL_KEY.CALL_PATIENT,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.CALL_PATIENT,
      responsePopupTitle: RESPONSE_POPUP_KEY.CALL_PATIENT,
      modalTitle: 'CALL PATIENT',
      style: DIMENSION.STANDARD,
    },
    {
      Icon: MessageIcon,
      Component: TextPatient,
      label: 'Text',
      key: MODAL_KEY.TEXT_PATIENT,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.TEXT_PATIENT,
      responsePopupTitle: RESPONSE_POPUP_KEY.TEXT_PATIENT,
      modalTitle: 'TEXT PATIENT',
      style: DIMENSION.STANDARD,
    },
    {
      Icon: EmailIcon,
      Component: EmailPatient,
      label: 'Email',
      key: MODAL_KEY.EMAIL_PATIENT,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.EMAIL_PATIENT,
      responsePopupTitle: RESPONSE_POPUP_KEY.EMAIL_PATIENT,
      modalTitle: 'EMAIL PATIENT',
      style: DIMENSION.REGULAR,
    },
  ], []);

  const createPopupConfig = useCallback((key = '') => {
    const popup = {
      heading: 'Message',
      type: POPUP_TYPE.INFO,
      message: 'API response appears here...',
    };

    if (!ModalCollection.has(key)) {
      return popup;
    }

    return ModalCollection.get(key) || popup;
  }, [ModalCollection]);

  const ActionButtons = new Map([
    [true, (ButtonsWithConfig.map(({ label, Icon }) => (
      <Button size='small' disabled startIcon={<Icon />}><span className='bold underline font-16 transform-none'>{label}</span></Button>
    )))],
    [false, (ButtonsWithConfig.map(({
      key,
      label,
      modalTitle,
      Icon,
      Component,
      style,
      closeConfirmationTitle,
      responsePopupTitle,
    }) => (
      <Fragment key={key}>
        <Modal
          title={modalTitle}
          open={ModalCollection.has(key)}
          style={style}
          closeConfirmation={ModalCollection.has(closeConfirmationTitle)}
          onCloseConfirmationYes={handleMultipleModalClose.bind(this, { keys: [key, closeConfirmationTitle] })}
          onCloseConfirmationNo={handleMultipleModalClose.bind(this, { keys: [closeConfirmationTitle] })}
          responsePopup={ModalCollection.has(responsePopupTitle)}
          responsePopupType={createPopupConfig(responsePopupTitle).type}
          responsePopupHeading={createPopupConfig(responsePopupTitle).heading}
          responsePopupMessage={createPopupConfig(responsePopupTitle).message}
          onResponsePopupOk={handleMultipleModalClose.bind(this, { keys: [responsePopupTitle] })}
        >
          <Component
            orderId={orderId}
            patientId={patientId}
            doctorId={doctorId}
            onClose={handleModalClose.bind(this, { key })}
            calling={calling}
            product={product}
            callSummaryReasons={callSummaryReasons}
            messageTemplates={messageTemplates}
            emailTemplates={emailTemplates}
            emailTemplatesV2={emailTemplatesV2}
            emailReasons={emailReasons}
            callPatientInfo={callPatientInfo}
            sendTextInfo={sendTextInfo}
            sendEmailInfo={sendEmailInfo}
            callPatient={callPatient}
            saveCallSummary={saveCallSummary}
            sendText={sendText}
            sendEmail={sendEmail}
            getEmails={getEmails}
            getNotesTabInfo={getNotesTabInfo}
          />
        </Modal>
        <Button
          size='small'
          className='primary-button'
          startIcon={<Icon />}
          onClick={handleModalOpen.bind(this, { key, modal: {} })}
        >
          <span className='bold underline font-16 transform-none'>{label}</span>
        </Button>
      </Fragment>
    )))]
  ]);

  const loading = useMemo(() => ({
    patient: !!patient.api.loading,
    product: !!product.api.loading,
  }), [patient.api.loading, product.api.loading]);

  const ActionCollection = new Map([
    [true, (
      <div className='flex justify-content-space-between'>
        {ActionButtons.get(loading.patient)}
      </div>
    )],
    [false, <Fragment />],
  ]);

  return (
    <div className='flex flex-column gap-16'>
      {
        (loading.patient || time) && (
          <div className='flex d-none'>
            {StartTime.get(loading.patient)}
          </div>
        )
      }

      <div className='flex'>
        {Name.get(loading.patient)}
      </div>

      <div className='flex'>
        {Storefront.get(loading.product)}
      </div>

      <div className='flex'>
        {Shipping.get(loading.product)}
      </div>

      <div className='flex'>
        <label className='m-r-2'>DOB:</label>
        {Dob.get(loading.patient)}
      </div>

      <div className='flex'>
        <label className='m-r-2'>Gender:</label>
        {Gender.get(loading.patient)}
      </div>

      <div className='flex'>
        <label className='m-r-2'>Height:</label>
        {Height.get(loading.patient)}

        <span className='hr m-l-2 m-r-2'></span>

        <label className='m-r-2'>Weight:</label>
        {Weight.get(loading.patient)}
      </div>

      <div className='flex'>
        <label className='m-r-2'>BMI:</label>
        {BMI.get(loading.patient)}
      </div>

      {ActionCollection.get(hasActions)}
    </div>
  );
});

export default PatientInformation;
